import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ProjectDetail, ProjectListItem, ProjectListResponse, ProjectRefDto, ProjectSortRequestResponse } from '../../shared/api-types.js';
import { api } from '../api.js';
import { PATHS } from '../paths.js';
import { useMe } from '../shell/me-context.js';
import {
  Button,
  Chip,
  DataTable,
  Dialog,
  EM_DASH,
  Icon,
  IconButton,
  LinkButton,
  Menu,
  Page,
  PageHead,
  ProjectChip,
  RelativeTime,
  SelectField,
  Spinner,
  StateView,
  StatusPill,
  TextField,
  failureDetail,
  formatCount,
  pluralise,
  useIsMobile,
  useNow,
  useRequest,
  useToast,
  useUrlSort,
  type DataTableColumn,
  type DataTableStatus,
  type DirectionWords,
  type MenuEntry,
} from '../ui/index.js';
import {
  DEFAULT_PROJECT_FILTERS,
  PROJECT_DEFAULT_SORT,
  PROJECT_KIND_FILTERS,
  PROJECT_KIND_FILTER_LABELS,
  PROJECT_KIND_LABELS,
  PROJECT_SORT_NAMESPACE,
  PROJECT_SORT_OPTIONS,
  activeProjectFilterCount,
  namedByWord,
  parseProjectFilters,
  partyInSentence,
  projectApiPath,
  projectListPath,
  projectSharePath,
  projectsEmptyState,
  shareRevocable,
  undoRenameRequest,
  writeProjectFilters,
  type ProjectColumnKey,
  type ProjectFilters,
  type ProjectKindFilter,
} from './lib/projects.js';
import { SORT_ASKING_TEXT, SORT_NOW_LABEL, sortInFlight, sortNowOffer, type SortNowOffer } from './lib/project-sort.js';
import { DeleteProjectDialog, MergeProjectDialog, NewProjectDialog, RenameProjectDialog, ShareProjectDialog } from './project-dialogs.js';
import './pages.css';

// The Projects destination (design §7.2; owner ruling 8 of 2026-10-02, ADR 0017): one table of the person's own
// projects and those shared with them (one result set, 04-data §1.2.1), every column sortable with the sort in the URL,
// a name search and one structured filter — Kind — as removable chips, the empty state truthful to the landing
// backfill, and the row actions of design §7.4. Shared rows carry S1's facts only: provenance, counts and recency are
// not the reader's to see and render as "—" (not recorded here), never as a number.

const SEARCH_DEBOUNCE_MS = 300;
/**
 * While a sorting job of the reader's waits or runs, the list is read again this often, so the status line moves without a
 * reload (design §2.11, the Assistant page's idiom) — at most SORT_POLL_MAX times in a row, a bounded watch.
 */
export const SORT_POLL_INTERVAL_MS = 15_000;
const SORT_POLL_MAX = 120;
const KIND_OPTIONS = PROJECT_KIND_FILTERS.map((kind) => ({ value: kind, label: PROJECT_KIND_FILTER_LABELS[kind] }));

type DialogState =
  | { kind: 'new' }
  | { kind: 'rename'; project: ProjectDetail }
  | { kind: 'merge'; project: ProjectDetail; into: ProjectRefDto | null }
  | { kind: 'share'; project: ProjectDetail }
  | { kind: 'delete'; project: ProjectDetail }
  | null;

/** A cell that is not the reader's to see on a shared row (S1): a dash, said so for assistive technology (`role="img"` so the name is announced; a bare span's is not). */
function NotShared() {
  return <span className="muted" role="img" aria-label="Not shown for a project shared with you">{EM_DASH}</span>;
}

export interface ProjectsTableProps {
  rows: readonly ProjectListItem[];
  status: DataTableStatus;
  empty: { title: string; description: string; action?: ReactNode };
  filtered?: { active: boolean; onClear: () => void; title?: string; description?: string };
  highlightId?: string | null;
  footer?: ReactNode;
  /** The row actions of an own row; omitted, the table renders only the Open link of a shared row. */
  onAction?: (kind: 'rename' | 'merge' | 'share' | 'delete', row: ProjectListItem) => void;
  /** The row whose detail is being fetched for a dialog; its trigger shows the wait. */
  pendingRowId?: string | null;
}

/**
 * The Kind column's direction words: its rank is own 0, shared 1, so ascending puts the person's own first. Why its
 * own words: the enum type's "first to last" names an order no reader can picture; "Kind, yours first" / "Kind, shared
 * first" say what the sort does (04-data §1.2).
 */
export const PROJECT_KIND_DIRECTION_WORDS: DirectionWords = Object.freeze({ asc: 'yours first', desc: 'shared first' });

/**
 * The table itself, presentational: columns, their sort values and the mobile card roles. Exported so its header
 * contract — every column sorts, `aria-sort` on the `<th>`, the default marked — renders under node in the unit test.
 */
export function ProjectsTable({ rows, status, empty, filtered, highlightId, footer, onAction, pendingRowId }: ProjectsTableProps) {
  // Folding (04-data §1.7; ADR 0168): who named a project and how many it is shared with (supplementary) first, then its
  // kind, then the counts a project is compared by, and Last used — the default sort — last. The name and the actions
  // never fold.
  const columns = useMemo<DataTableColumn<ProjectListItem>[]>(() => [
    {
      key: 'name',
      header: 'Name',
      type: 'text',
      sortValue: (project) => project.name,
      card: 'title',
      cell: (project) => <ProjectChip project={project} />,
    },
    {
      key: 'kind',
      header: 'Kind',
      type: 'enum',
      directionWords: PROJECT_KIND_DIRECTION_WORDS,
      sortValue: (project) => (project.kind === 'own' ? 0 : 1),
      card: 'meta',
      fold: 3,
      cell: (project) => (
        <span className="project-kind">
          <StatusPill tone={project.kind === 'own' ? 'neutral' : 'info'} icon={project.kind === 'own' ? 'user' : 'users'}>{PROJECT_KIND_LABELS[project.kind]}</StatusPill>
          {/* Why `partyInSentence` (the owner's ruling of 2026-10-11): a withheld sharer reads "by a colleague (no longer has access)", as the digest says it. */}
          {project.kind === 'shared' && project.sharedBy ? <span className="text-xs secondary"> by {partyInSentence(project.sharedBy)}</span> : null}
        </span>
      ),
    },
    {
      key: 'meetings',
      header: 'Meetings',
      type: 'number',
      numeric: true,
      sortValue: (project) => project.meetings,
      card: 'field',
      fold: 4,
      cell: (project) => (project.meetings === null ? <NotShared /> : formatCount(project.meetings)),
    },
    {
      key: 'open_todos',
      header: 'Open to-dos',
      type: 'number',
      numeric: true,
      sortValue: (project) => project.openTodos,
      card: 'field',
      fold: 5,
      cell: (project) => (project.openTodos === null ? <NotShared /> : formatCount(project.openTodos)),
    },
    {
      key: 'last_used_at',
      header: 'Last used',
      type: 'date',
      sortValue: (project) => project.lastUsedAt,
      card: 'field',
      fold: 6,
      cell: (project) => (project.lastUsedAt === null ? <NotShared /> : <RelativeTime value={project.lastUsedAt} />),
    },
    {
      key: 'named_by',
      header: 'Named by',
      type: 'text',
      sortValue: (project) => namedByWord(project.namedBy),
      card: 'field',
      fold: 1,
      cell: (project) => {
        const word = namedByWord(project.namedBy);
        if (word === null) return <NotShared />;
        return word === 'Calis' ? <span className="project-named-by"><Icon name="sparkles" size={14} /> Calis</span> : word;
      },
    },
    {
      key: 'shared_with',
      header: 'Shared with',
      type: 'number',
      numeric: true,
      sortValue: (project) => (project.kind === 'shared' ? null : project.sharedWith),
      card: 'field',
      fold: 2,
      cell: (project) => (project.kind === 'shared' ? <NotShared /> : formatCount(project.sharedWith)),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      name: 'Actions',
      sortable: false,
      card: 'actions',
      align: 'end',
      cell: (project) => {
        if (project.kind === 'shared') return <LinkButton to={PATHS.project(project.id)} size="sm" variant="secondary">Open</LinkButton>;
        if (!onAction) return null;
        const entries: MenuEntry[] = [
          { id: 'rename', label: 'Rename…', icon: 'edit', onSelect: () => onAction('rename', project) },
          { id: 'merge', label: 'Merge into…', icon: 'link', onSelect: () => onAction('merge', project) },
          { id: 'share', label: 'Share…', icon: 'users', onSelect: () => onAction('share', project) },
          { id: 'delete', label: 'Delete', icon: 'trash', danger: true, onSelect: () => onAction('delete', project) },
        ];
        // Why `busy` and not `loading`: loading disables the button, and a focused button that becomes disabled drops
        // focus to the body — the dialog then records the body as its opener and restores focus to nothing on close
        // (03-components §4.1). Busy shows the wait and keeps the trigger focusable; `openAction` refuses a second
        // fetch while one is in flight.
        return (
          <Menu
            label={`Actions for ${project.name}`}
            entries={entries}
            trigger={(props) => <IconButton {...props} icon="more-horizontal" label={`Actions for ${project.name}`} size="sm" busy={pendingRowId === project.id} />}
          />
        );
      },
    },
  ], [onAction, pendingRowId]);

  return (
    <DataTable<ProjectListItem, ProjectColumnKey | 'actions'>
      caption="Projects"
      failureNoun="your projects"
      columns={columns}
      rows={rows}
      rowKey={(project) => project.id}
      status={status}
      sort={{ defaultSort: PROJECT_DEFAULT_SORT, namespace: PROJECT_SORT_NAMESPACE }}
      clientSort
      empty={empty}
      filtered={filtered}
      highlightId={highlightId}
      mobile="cards"
      footer={footer}
    />
  );
}

export interface ProjectSortControlProps {
  offer: SortNowOffer;
  /** The press is in flight: the button shows its loading state (03-components §2: width stable, one request per press). */
  requesting: boolean;
  /** Between an accepted press and the next reading: the page does not yet know where the job stands. */
  asking: boolean;
  onSort: () => void;
}

/**
 * "Sort my to-dos now" in the page head (owner ruling 11's Q12(c); design §2.11; ADR 0018): a secondary button — "New
 * project" stays the head's one primary — or, where it may not be pressed, the reason in its place, never a disabled
 * button as the only explanation (03-components §2): Running, the cooldown, or the permission sentence for a reader
 * who holds neither grant path; nothing at all where the pass never runs here. Beneath, muted, a run that sorted nothing
 * (Failed or Skipped, 05-content §6) and the last pass's time and counts — never a word of a to-do. Exported so the unit
 * test renders each state under node.
 *
 * Why one live region that stays mounted (01-foundations §7.3): the outcome arrives by the page's polling, not by an act
 * of the reader's, so the status, the reason and the counts sit in one `role="status"` region rendered in every state —
 * always the second slot, after the button's (empty when no button is offered, and hidden then) — and only its words
 * change; the spinner inside it is decorative. Why focus moves to it: when a press is accepted the focused button gives
 * way to the asking line, and focus would otherwise fall to the document.
 */
export function ProjectSortControl({ offer, requesting, asking, onSort }: ProjectSortControlProps) {
  const region = useRef<HTMLDivElement>(null);
  const pressed = useRef(false);
  const offered = !asking && (offer.kind === 'ready' || offer.kind === 'pending');
  const hadButton = useRef(offered);
  useEffect(() => {
    if (hadButton.current && !offered && pressed.current) {
      pressed.current = false;
      const active = document.activeElement;
      if (active === null || active === document.body) region.current?.focus({ preventScroll: true });
    }
    hadButton.current = offered;
  }, [offered]);
  if (offer.kind === 'off') return null;
  const press = (): void => {
    pressed.current = true;
    onSort();
  };
  const status = (text: string) => (
    <span className="project-sort__status text-sm secondary">
      <Spinner size={16} decorative />
      <span>{text}…</span>
    </span>
  );
  const line = (() => {
    if (offer.kind === 'not_granted') return <p className="project-sort__reason text-sm muted">{offer.text}</p>;
    if (asking && offer.kind !== 'running') return status(SORT_ASKING_TEXT);
    if (offer.kind === 'cooldown') return <p className="project-sort__reason text-sm muted">{offer.text}</p>;
    if (offer.kind === 'running' || offer.kind === 'pending') return status(offer.text);
    return offer.outcome ? (
      <p className="project-sort__reason text-sm secondary">
        {offer.outcome.text} <span className="muted">(<RelativeTime value={offer.outcome.at} />)</span>
      </p>
    ) : null;
  })();
  const last = offer.kind !== 'not_granted' && offer.last ? (
    <p className="project-sort__last text-sm muted">
      Last sorted <RelativeTime value={offer.last.at} /> · {offer.last.counts}
    </p>
  ) : null;
  return (
    <div className="project-sort">
      <div className="project-sort__control">
        {offered ? <Button variant="secondary" icon="refresh" onClick={press} loading={requesting}>{SORT_NOW_LABEL}</Button> : null}
      </div>
      <div ref={region} className="project-sort__lines" role="status" aria-live="polite" tabIndex={-1}>
        {line}
        {last}
      </div>
    </div>
  );
}

export default function ProjectsPage() {
  const me = useMe();
  const toast = useToast();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseProjectFilters(params), [params]);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const [sort] = useUrlSort(PROJECT_SORT_OPTIONS);
  const projectsOn = me.integrations.projects !== 'off';
  const path = projectListPath(filters, sort);
  // Why nothing is asked while the floor is off: the route would answer 403 feature_not_configured; the page says so itself.
  const request = useRequest<ProjectListResponse>(
    (signal) => (projectsOn ? api.get<ProjectListResponse>(path, { signal }) : new Promise<never>(() => {})),
    [path, projectsOn],
  );

  const setFilters = useCallback((next: ProjectFilters) => {
    setParams((current) => writeProjectFilters(current, next), { replace: true });
  }, [setParams]);

  // --- The search field, committed to the address after a pause ---------------------------------------------------------
  // Why the writes it has sent are remembered (the transcript search's rule): the router applies an address change in a
  // transition, so letters typed while one lands are newer than it; an address coming back with a value this field sent
  // is not copied into the field, and only an address changed from elsewhere (a chip, Back, Clear all) is.
  const [draft, setDraft] = useState(filters.q);
  const sent = useRef<string[]>([]);
  useEffect(() => {
    const own = sent.current.indexOf(filters.q);
    if (own !== -1) {
      sent.current = sent.current.slice(own + 1);
      return;
    }
    sent.current = [];
    setDraft((current) => (current.trim() === filters.q ? current : filters.q));
  }, [filters.q]);
  const commitSearch = useCallback((next: string) => {
    const normalised = next.trim();
    const latest = sent.current.length > 0 ? sent.current[sent.current.length - 1] : filtersRef.current.q;
    if (normalised === latest) return;
    sent.current = [...sent.current, normalised];
    setFilters({ ...filtersRef.current, q: normalised });
  }, [setFilters]);
  useEffect(() => {
    const handle = window.setTimeout(() => commitSearch(draft), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft, commitSearch]);

  // --- The Kind filter: a select on desktop, a sheet with Apply on a phone (03-components §10) ----------------------------
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDraft, setSheetDraft] = useState<ProjectKindFilter>(filters.kind);
  const setKind = useCallback((kind: ProjectKindFilter) => setFilters({ ...filtersRef.current, kind }), [setFilters]);
  // Clear-all restores rows, not the arrangement (04-data §1.5): the sort in the URL is left alone.
  const clearAll = useCallback(() => {
    setDraft('');
    sent.current = [''];
    setFilters({ ...DEFAULT_PROJECT_FILTERS });
  }, [setFilters]);
  const activeCount = activeProjectFilterCount(filters);
  const anyFilter = activeCount > 0;

  const kindField = (value: ProjectKindFilter, set: (next: ProjectKindFilter) => void) => (
    <SelectField label="Kind" value={value} onChange={(event) => set(event.target.value as ProjectKindFilter)} options={KIND_OPTIONS} className="filter-bar__status" />
  );

  // --- Row actions and their dialogs ---------------------------------------------------------------------------------------
  const [dialog, setDialog] = useState<DialogState>(null);
  const [pendingRowId, setPendingRowId] = useState<string | null>(null);
  const pending = useRef(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  /** A row action needs the project's detail (counts, shares, aliases) — fetched when chosen, so the list stays one request. */
  const openAction = useCallback(async (kind: 'rename' | 'merge' | 'share' | 'delete', row: ProjectListItem): Promise<void> => {
    // One fetch at a time: the trigger stays focusable while it waits (see the row's IconButton), so a second press is refused here.
    if (pending.current) return;
    pending.current = true;
    setPendingRowId(row.id);
    const result = await api.get<ProjectDetail>(projectApiPath(row.id));
    pending.current = false;
    setPendingRowId(null);
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t open {row.name}. {failureDetail(result)}</>);
      if (result.code === 'not_found') request.reload();
      return;
    }
    setDialog(kind === 'merge' ? { kind, project: result.data, into: null } : { kind, project: result.data });
  }, [request, toast]);
  const onAction = useCallback((kind: 'rename' | 'merge' | 'share' | 'delete', row: ProjectListItem) => {
    void openAction(kind, row);
  }, [openAction]);

  // Why the detail and not the server's message: it says why and what next, a refusal's next step included (`failureDetail`).
  const failed = (what: string, detail: ReactNode): void => {
    toast.error(<>We couldn&rsquo;t {what}. {detail}</>);
  };

  // Why the request is built from the detail held before the rename (`undoRenameRequest`; owner ruling 19 of 2026-10-04:
  // "the toast's Undo restores the former name and forgets the abandoned one, so 'Also known as' is as it was before the
  // rename"): the server cannot tell an Undo from a rename back by hand, nor whether the abandoned name was a former name
  // before the rename — only this page, holding both details, can; the helper says `undo: true` exactly when forgetting
  // puts the list back, and sends a plain rename back otherwise.
  const undoRename = async (before: ProjectDetail, renamed: ProjectDetail): Promise<void> => {
    const result = await api.patch<ProjectDetail>(projectApiPath(renamed.id), undoRenameRequest(before, renamed));
    if (!result.ok) failed('restore the name', failureDetail(result));
    request.reload();
  };
  const undoMerge = async (absorbed: ProjectDetail): Promise<void> => {
    const result = await api.post<ProjectDetail>(`${projectApiPath(absorbed.id)}/unmerge`);
    if (!result.ok) failed('undo the merge', failureDetail(result));
    request.reload();
  };
  const undoShare = async (detail: ProjectDetail, recipientId: string): Promise<void> => {
    const share = detail.shares.find((row) => row.recipient.id === recipientId && shareRevocable(row.state));
    const result = share ? await api.delete<ProjectDetail>(projectSharePath(detail.id, share.id)) : null;
    if (!result || !result.ok) failed('end the share', result ? failureDetail(result) : 'The share could not be found.');
    request.reload();
  };

  // --- Sort my to-dos now (owner ruling 11's Q12(c); design §2.11) ---------------------------------------------------------
  // Why the flag: between an accepted press and the next reading the page would offer the button again for a moment; it
  // holds the asking line until that reading lands, whatever it says.
  const now = useNow(30_000);
  const [requestingSort, setRequestingSort] = useState(false);
  const [askingSort, setAskingSort] = useState(false);
  const [sortPolls, setSortPolls] = useState(0);
  useEffect(() => {
    setAskingSort(false);
  }, [request.data]);
  const sortWatching = askingSort || sortInFlight(request.data?.sort);
  useEffect(() => {
    if (!sortWatching || sortPolls >= SORT_POLL_MAX) return;
    const handle = window.setTimeout(() => {
      setSortPolls((count) => count + 1);
      request.reload();
    }, SORT_POLL_INTERVAL_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortWatching, sortPolls]);
  const requestSort = async (): Promise<void> => {
    if (requestingSort) return;
    setRequestingSort(true);
    const result = await api.post<ProjectSortRequestResponse>('/api/projects/sort');
    setRequestingSort(false);
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t ask Calis to sort your to-dos. {failureDetail(result)}</>);
      // A 409 or 429 means the reading moved on (a cooldown, nothing to sort, the day's four): show the current one.
      if (result.code === 'conflict' || result.code === 'rate_limited') request.reload();
      return;
    }
    // Why "it goes first" either way: that the request goes ahead of other work is the one outcome the toast confirms.
    toast.success(result.data.alreadyQueued ? 'Your to-dos were already waiting to be sorted — they now go first.' : 'Calis will sort your to-dos shortly — it goes first.');
    setAskingSort(true);
    setSortPolls(0);
    request.reload();
  };

  // --- States -----------------------------------------------------------------------------------------------------------
  if (!projectsOn) {
    return (
      <Page measure="wide">
        <PageHead title="Projects" />
        <StateView kind="not-configured" title="Projects are not switched on" description="This installation has projects switched off, so nothing is sorted into them and nothing can be filed. An operator can switch them on." minHeight="40dvh" />
      </Page>
    );
  }

  const data = request.data;
  const status = request.status === 'loading'
    ? { kind: 'loading' as const, rowCount: 6 }
    : request.status === 'error' && request.error
      ? { kind: 'error' as const, failure: request.error, onRetry: request.reload }
      : { kind: 'ready' as const };
  const truncated = data !== null && data.items.length < data.matching;
  const empty = projectsEmptyState(data?.backfill ?? 'none');
  const newProjectButton = (variant: 'primary' | 'secondary') => (
    <Button variant={variant} icon="plus" onClick={() => setDialog({ kind: 'new' })}>New project</Button>
  );
  const sortControl = data ? <ProjectSortControl offer={sortNowOffer(data.sort, now)} requesting={requestingSort} asking={askingSort} onSort={() => void requestSort()} /> : null;
  // Why two orders (02-patterns §2.1): on desktop the actions are right-aligned and the one primary holds the trailing
  // edge, the secondary before it; on a phone the head stacks, actions full-width beneath the subtitle, the primary first.
  const headActions = isMobile ? (
    <>
      {newProjectButton('primary')}
      {sortControl}
    </>
  ) : (
    <>
      {sortControl}
      {newProjectButton('primary')}
    </>
  );

  return (
    <Page measure="wide">
      <PageHead
        title="Projects"
        subtitle="The clients, products and initiatives your meetings and to-dos belong to — named by Calis as it summarises, or by you."
        actions={headActions}
        // Why: the sort control carries a status beside the buttons; at the medium widths it goes beneath the subtitle (shell.css).
        className={data && data.sort.state !== 'off' ? 'page-head--status-actions' : undefined}
      />
      <div className="filter-bar stack stack--3">
        <div className="filter-bar__controls">
          <form
            role="search"
            aria-label="Search projects"
            className="filter-bar__search"
            onSubmit={(event) => {
              event.preventDefault();
              commitSearch(draft);
            }}
          >
            <Icon name="search" size={16} className="filter-bar__search-icon" />
            <TextField
              label="Search project names"
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              spellCheck={false}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              className="filter-bar__search-field"
            />
            {draft !== '' ? (
              <IconButton
                icon="x"
                label="Clear search"
                size="sm"
                className="filter-bar__search-clear"
                onClick={() => {
                  setDraft('');
                  commitSearch('');
                }}
              />
            ) : null}
          </form>
          {isMobile ? (
            <Button variant="secondary" icon="filter" onClick={() => { setSheetDraft(filters.kind); setSheetOpen(true); }} aria-haspopup="dialog">
              {filters.kind !== 'all' ? 'Filters (1)' : 'Filters'}
            </Button>
          ) : (
            kindField(filters.kind, setKind)
          )}
        </div>
        {anyFilter || data ? (
          <div className="filter-bar__chips split">
            <ul className="cluster" role="list" aria-label="Active filters">
              {filters.kind !== 'all' ? (
                <li>
                  <Chip tone="accent" selected onRemove={() => setKind('all')} removeLabel={`Remove filter: Kind: ${PROJECT_KIND_FILTER_LABELS[filters.kind]}`}>Kind: {PROJECT_KIND_FILTER_LABELS[filters.kind]}</Chip>
                </li>
              ) : null}
              {filters.q !== '' ? (
                <li>
                  <Chip tone="accent" selected onRemove={() => { setDraft(''); commitSearch(''); }} removeLabel={`Remove filter: search ${filters.q}`}>Search: {filters.q}</Chip>
                </li>
              ) : null}
              {activeCount >= 2 ? (
                <li>
                  <Button variant="link" size="sm" onClick={clearAll}>Clear all</Button>
                </li>
              ) : null}
            </ul>
            {data ? (
              <p className="filter-bar__count text-sm secondary num" role="status" aria-live="polite">
                {anyFilter ? `${formatCount(data.matching)} of ${pluralise(data.total, { one: '# project', other: '# projects' })}` : pluralise(data.total, { one: '# project', other: '# projects' })}
              </p>
            ) : null}
          </div>
        ) : null}
        <Dialog
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Filters"
          size="sm"
          initialFocus="first"
          actions={
            <>
              <Button variant="secondary" onClick={() => setSheetOpen(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => { setKind(sheetDraft); setSheetOpen(false); }}>Apply</Button>
            </>
          }
        >
          <div className="stack stack--3">{kindField(sheetDraft, setSheetDraft)}</div>
        </Dialog>
      </div>

      <ProjectsTable
        rows={data?.items ?? []}
        status={status}
        empty={{ title: empty.title, description: empty.description, action: newProjectButton('secondary') }}
        filtered={{
          active: anyFilter,
          onClear: clearAll,
          title: filters.kind === 'shared' && filters.q === '' ? 'Nothing is shared with you yet' : filters.kind === 'own' && filters.q === '' ? 'You have no projects of your own yet' : 'No project matches that search',
          description: filters.kind === 'shared' && filters.q === ''
            ? 'A colleague can share one of their projects with you by name; it appears here while the share stands.'
            : filters.q !== ''
              ? 'Only names are searched. Try another word, or clear the filters to see every project.'
              : undefined,
        }}
        highlightId={highlightId}
        onAction={onAction}
        pendingRowId={pendingRowId}
        // Why only when rows were left out: the count line already states the figure; the footer's one job is position
        // and total past the row cap (03-components §11).
        footer={data && truncated ? (
          <p className="text-sm secondary num">
            Showing the first {formatCount(data.items.length)} of {formatCount(data.matching)} projects. Narrow the search to see the rest.
          </p>
        ) : null}
      />

      <NewProjectDialog
        open={dialog?.kind === 'new'}
        onClose={() => setDialog(null)}
        onCreated={(created) => {
          setDialog(null);
          setHighlightId(created.id);
          toast.success(<>Added <strong>{created.name}</strong>.</>);
          request.reload();
        }}
      />
      <RenameProjectDialog
        project={dialog?.kind === 'rename' ? dialog.project : null}
        onClose={() => setDialog(null)}
        onRenamed={(renamed) => {
          const before = dialog?.kind === 'rename' ? dialog.project : null; // the detail held before the rename: what the Undo is built from
          setDialog(null);
          toast.success(<>Project renamed to <strong>{renamed.name}</strong>.</>, before ? { action: { label: 'Undo', onClick: () => void undoRename(before, renamed) } } : undefined);
          request.reload();
        }}
        onMergeInstead={(into) => {
          if (dialog?.kind === 'rename') setDialog({ kind: 'merge', project: dialog.project, into });
        }}
      />
      <MergeProjectDialog
        project={dialog?.kind === 'merge' ? dialog.project : null}
        initialInto={dialog?.kind === 'merge' ? dialog.into : null}
        onClose={() => setDialog(null)}
        onMerged={(response, absorbed) => {
          setDialog(null);
          toast.success(<>Merged <strong>{absorbed.name}</strong> into <strong>{response.project.name}</strong>.</>, { action: { label: 'Undo', onClick: () => void undoMerge(absorbed) } });
          request.reload();
        }}
      />
      <ShareProjectDialog
        executiveOn={me.integrations.executive === 'on'}
        project={dialog?.kind === 'share' ? dialog.project : null}
        onClose={() => setDialog(null)}
        onShared={(detail, recipient) => {
          setDialog(null);
          toast.success(<>Shared <strong>{detail.name}</strong> with {recipient.label}.</>, { action: { label: 'Undo', onClick: () => void undoShare(detail, recipient.id) } });
          request.reload();
        }}
      />
      <DeleteProjectDialog
        project={dialog?.kind === 'delete' ? dialog.project : null}
        onClose={() => setDialog(null)}
        onDeleted={(project) => {
          setDialog(null);
          toast.success(<>Deleted <strong>{project.name}</strong>. Its meetings and to-dos keep everything but the label.</>);
          request.reload();
        }}
      />
    </Page>
  );
}
